/**
 * Which report parameter says who a subscription runs as, and what to put in it.
 *
 * Lifted out of useResolvedValues, where it started life as a lookup detail. It earns a module of
 * its own now that the same judgement decides a stored value and not just which dropdown gets
 * filled: two copies of "is this an identity parameter?" that drifted apart would mean a form
 * resolving its lists as one thing and saving another, which is precisely the split that caused
 * the failure below.
 *
 * Kept on the client rather than moved into the API, and that is a decision rather than an
 * accident. The value has to be on screen before it is saved — it changes whose data the report
 * renders, so it cannot be a server-side surprise — which means the form has to make this
 * judgement anyway. Making it twice, once here and once in SubscriptionService, is how the two
 * come to disagree. The API stores what it is given.
 */
import type { ReportParameterDefinition, ReportParameterValue } from '@/types';
import { sameValue } from './valueMatching';

/**
 * Parameter names that mean "the person this report is being run for".
 *
 * Reports on MANDW01 scope their own data this way: the parameter's default is an expression the
 * report server evaluates, so it resolves to whichever account asked. PBIRS REST creates every
 * portal subscription as the portal's service account, which is a member of nothing, so that
 * account is what the report resolves — both when the form asks for a list and when the
 * subscription runs at 6am. Measured on /Sales/Scope 3: as `KEY\svc_reportportal` the Country Code
 * list has 0 entries; as a real person it has 9, and Head Office then has 5,845.
 *
 * That zero is not merely an empty dropdown, it is a broken subscription. Every portal-created
 * subscription on that report failed every run with "Default value or value provided for the
 * report parameter 'CountryCode' is not a valid value" — a stored CountryCode cannot be valid when
 * the list it is checked against is empty. The subscriptions on that report that DO send are
 * exactly the ones whose identity resolves to somebody: `b1e928a8`, which stores a User value even
 * though the service account owns it, and one owned by a real person, which resolves her own.
 *
 * Kept to an explicit list rather than a pattern, and that matters more now than it did when this
 * only shaped a dropdown. A parameter merely called "Owner" or "Manager" is a filter over other
 * people, not a statement about the caller; setting one of those would not empty a list, it would
 * quietly narrow the report to the wrong scope and still send.
 */
export const IDENTITY_PARAMETER_NAMES = new Set(['user', 'username', 'userid', 'upn', 'currentuser']);

/** Every parameter some other parameter's value list is computed from. */
export function upstreamParameterNames(definitions: ReportParameterDefinition[]): Set<string> {
  const names = new Set<string>();
  for (const d of definitions) for (const dep of d.dependencies ?? []) names.add(dep);
  return names;
}

/**
 * Whether this parameter is the one that says who the report is being run for.
 *
 * Two conditions past the name, both narrowing. Something else's value list has to actually depend
 * on it: without a declared dependency the portal has no evidence the report scopes anything by
 * this parameter, and writing an account into it on that much would change whose data a report
 * renders on a guess. And it has to be single-valued — "who is asking" has one answer, and a
 * multi-valued parameter called User is a list of people to report ON.
 */
export function isIdentityParameter(
  def: ReportParameterDefinition,
  upstreamNames: Set<string>
): boolean {
  return IDENTITY_PARAMETER_NAMES.has(def.name.toLowerCase())
    && upstreamNames.has(def.name)
    && !def.multiValued;
}

/**
 * The identity parameters nothing has answered yet.
 *
 * The third condition, and the one that keeps this from overriding a decision somebody made. A
 * stored value wins whether it was typed on the form, carried in by a clone from a subscription
 * that already worked, or set years ago in Schedule Manager — in every one of those cases a person
 * chose whose data this report renders, and the portal has nothing to add.
 */
export function unsetIdentityParameters(
  definitions: ReportParameterDefinition[],
  upstreamNames: Set<string>,
  values: ReportParameterValue[]
): string[] {
  return definitions
    .filter(d =>
      isIdentityParameter(d, upstreamNames) &&
      (values.find(v => v.name === d.name)?.values.length ?? 0) === 0)
    .map(d => d.name);
}

/**
 * The values a create should be saved with: every unset identity parameter answered with the
 * account of the person filling the form in.
 *
 * Returns `values` ITSELF when there is nothing to add, so a report with no identity parameter
 * produces byte-for-byte the create request it produced before any of this existed. That is the
 * property worth having and worth testing: writing an account in changes whose data the report
 * renders, so it has to reach precisely the reports that are broken without it and no others.
 *
 * A blank account is treated the same way — unchanged values. It is deliberately not this
 * function's job to refuse the create; a pure mapping cannot tell the caller anything, and the one
 * place that refusal is any use is the form, where it can be said before the button is pressed
 * rather than discovered at 6am. See CreateSubscription.
 */
export function withIdentityAccount(
  definitions: ReportParameterDefinition[],
  values: ReportParameterValue[],
  account: string | null | undefined
): ReportParameterValue[] {
  const trimmed = account?.trim();
  if (!trimmed) return values;

  const names = unsetIdentityParameters(definitions, upstreamParameterNames(definitions), values);
  if (names.length === 0) return values;

  return [...values, ...names.map(name => ({ name, values: [trimmed] }))];
}

/**
 * The identity parameters currently answered with `account`.
 *
 * What the form reads back to the user, so the statement on screen describes the values that are
 * actually about to be saved rather than what the page intended to do to them. Compared with
 * {@link sameValue} for the same reason every other stored value is: PBIRS is not byte-consistent
 * about incidental whitespace, and a trailing space is not a different person.
 */
export function identityParametersSetTo(
  definitions: ReportParameterDefinition[],
  values: ReportParameterValue[],
  account: string | null | undefined
): string[] {
  const trimmed = account?.trim();
  if (!trimmed) return [];

  const upstreamNames = upstreamParameterNames(definitions);
  return definitions
    .filter(d => {
      const stored = values.find(v => v.name === d.name)?.values ?? [];
      return isIdentityParameter(d, upstreamNames)
        && stored.length === 1
        && sameValue(stored[0], trimmed);
    })
    .map(d => d.name);
}
